// components/NoStreamLayer.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import * as fabric from "fabric";
import { Plus } from "lucide-react";
import useRecorderContext from "../context/useRecorderContext";
import { getDisplaySize } from "../utils/resSize";
import { useCanvasStreamDetection } from "../hooks/useCanvasStreamDetection";

interface NoStreamLayerProps {
  canvasWidth: number;
  canvasHeight: number;
  wrapperWidth: number;
  wrapperHeight: number;
  ratio: string;
}

const NoStreamLayer: React.FC<NoStreamLayerProps> = ({
  canvasWidth,
  canvasHeight,
  wrapperWidth,
  wrapperHeight,
  ratio,
}) => {
  const {
    stageRef,
    canvasReady,
    recordingQuality,
    screenStream,
    webcamStream,
  } = useRecorderContext();
  const { hasAnyCanvasStream } = useCanvasStreamDetection();
  const backgroundRectRef = useRef<fabric.Rect | null>(null);
  const textRef = useRef<fabric.Textbox | null>(null);
  const [shouldShowLayer, setShouldShowLayer] = useState(false);
  const isSetupRef = useRef(false);

  // Fallback detection using stream states
  const hasWebcam = !!webcamStream;
  const hasScreen = !!screenStream;
  const hasAnyStream = hasWebcam || hasScreen;

  // Determine if we should show the layer
  useEffect(() => {
    const canvasDetectionWorking = hasAnyCanvasStream !== undefined;
    const shouldShow =
      canvasReady &&
      (canvasDetectionWorking ? !hasAnyCanvasStream : !hasAnyStream);

    const timeoutId = setTimeout(() => {
      setShouldShowLayer(shouldShow);
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [ratio, canvasReady, hasAnyCanvasStream, hasAnyStream]);

  const cleanupCanvasObjects = useCallback((canvas: fabric.Canvas) => {
    if (backgroundRectRef.current) {
      try {
        canvas.remove(backgroundRectRef.current);
      } catch (e) {
        // Ignore cleanup errors
      }
      backgroundRectRef.current = null;
    }
    if (textRef.current) {
      try {
        canvas.remove(textRef.current);
      } catch (e) {
        // Ignore cleanup errors
      }
      textRef.current = null;
    }
    isSetupRef.current = false;
  }, []);

  const setupNoStreamLayer = useCallback((canvas: fabric.Canvas) => {
    // Remove existing objects first
    cleanupCanvasObjects(canvas);

    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    let baseContentWidth;

    const { scale } = getDisplaySize(recordingQuality, ratio, wrapperWidth);
    let baseFontSize;
    let baseTopOffset;
    let baseBleed;

    if (ratio === "16:9") {
      baseFontSize = 16 * 1.5;
      baseTopOffset = 64;
      baseBleed = 32;
      baseContentWidth = 340;
    } else if (ratio === "1:1") {
      baseFontSize = 16 * 1.5;
      baseTopOffset = 64;
      baseBleed = 32;
      baseContentWidth = 340;
    } else {
      baseFontSize = 12;
      baseTopOffset = 42;
      baseBleed = 16;
      baseContentWidth = 160;
    }

    const scaledFontSize = baseFontSize / scale;
    const contentWidth = baseContentWidth / scale;
    const topOffset = baseTopOffset / scale;
    const bleedMargin = baseBleed / scale;

    const backgroundRect = new fabric.Rect({
      left: bleedMargin,
      top: bleedMargin,
      width: canvasWidth - bleedMargin * 2,
      height: canvasHeight - bleedMargin * 2,
      fill: "black",
      opacity: 0.32,
      rx: 8 / scale,
      ry: 8 / scale,
      selectable: false,
      evented: false,
      data: { isNoStreamOverlay: true },
    });

    const text = new fabric.Textbox(
      "To Start Recording, Turn On Your Camera Or Share Your Screen",
      {
        left: centerX,
        top: centerY + topOffset,
        fontSize: scaledFontSize,
        fontFamily:
          "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        fill: "white",
        textAlign: "center",
        originX: "center",
        originY: "center",
        lineHeight: 1.6,
        width: contentWidth,
        selectable: false,
        evented: false,
        data: { isNoStreamOverlay: true },
      }
    );

    backgroundRectRef.current = backgroundRect;
    textRef.current = text;

    canvas.add(backgroundRect);
    canvas.add(text);

    // Bring to front
    canvas.bringObjectToFront(backgroundRect);
    canvas.bringObjectToFront(text);

    canvas.requestRenderAll();
    isSetupRef.current = true;
  }, [canvasWidth, canvasHeight, ratio, recordingQuality, wrapperWidth, cleanupCanvasObjects]);

  // Ensure objects stay on top after canvas re-renders
  const ensureObjectsOnTop = useCallback(() => {
    const canvas = stageRef?.current?.getCanvas();
    if (!canvas || !isSetupRef.current) return;

    if (backgroundRectRef.current && textRef.current) {
      const objects = canvas.getObjects();
      const bgExists = objects.includes(backgroundRectRef.current);
      const textExists = objects.includes(textRef.current);

      if (bgExists && textExists) {
        canvas.bringObjectToFront(backgroundRectRef.current);
        canvas.bringObjectToFront(textRef.current);
      } else {
        setupNoStreamLayer(canvas);
      }
    }
  }, [stageRef, setupNoStreamLayer]);

  // Main setup effect
  useEffect(() => {
    if (!shouldShowLayer || !stageRef?.current) {
      if (stageRef?.current) {
        const canvas = stageRef.current.getCanvas();
        if (canvas) {
          cleanupCanvasObjects(canvas);
        }
      }
      return;
    }

    const canvas = stageRef.current.getCanvas();
    if (!canvas) return;

    setupNoStreamLayer(canvas);

    // Subscribe to canvas after:render to ensure objects stay on top
    const handleAfterRender = () => {
      ensureObjectsOnTop();
    };

    canvas.on("after:render", handleAfterRender);

    return () => {
      canvas.off("after:render", handleAfterRender);
      cleanupCanvasObjects(canvas);
    };
  }, [stageRef, shouldShowLayer, setupNoStreamLayer, cleanupCanvasObjects, ensureObjectsOnTop]);

  if (!shouldShowLayer) {
    return null;
  }

  return (
    <>
      {/* Plus icon as DOM element - positioned absolutely over the canvas */}
      <div
        id="no-stream-layer-plus-icon"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -60%)",
          zIndex: 1000,
          pointerEvents: "none",
        }}
      >
        <Plus size={32} color="#fff" />
      </div>
    </>
  );
};

export default NoStreamLayer;
